"use client";

import { useState } from "react";
import { Compass, Sparkles } from "lucide-react";
import { DemoRunner } from "@/components/landing/demo-runner";
import { WaitlistModal } from "@/components/waitlist-modal";
import { trackCtaOpened } from "@/src/features/demo-contracts/analytics";
import type { DemoPreview } from "@/src/features/demo-contracts/types";
import type { GuidedDemoPersistedPreviewMap } from "@/src/features/demo-contracts/landing-preview-loader";

export interface GuidedPromptBoxSectionProps {
  samplePreview?: DemoPreview;
  persistedPreviewMap?: GuidedDemoPersistedPreviewMap;
}

export function GuidedPromptBoxSection({
  samplePreview,
  persistedPreviewMap,
}: GuidedPromptBoxSectionProps) {
  const [visiblePreview, setVisiblePreview] = useState<DemoPreview | null>(
    null,
  );
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  function handleJoinWaitlist() {
    if (visiblePreview) {
      trackCtaOpened(visiblePreview.nicheSlug, visiblePreview.kind);
    }
    setWaitlistOpen(true);
  }

  return (
    <section
      id="guided-prompt-box"
      className="relative overflow-hidden border-y border-border bg-primary px-6 py-16 lg:px-8 lg:py-24"
    >
      {/* Evidence-wall atmosphere — no blur blobs, structured */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/8 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/8 to-transparent" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.015) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl space-y-10">
        {/* Header — structured, monospace metadata */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-6 inline-flex items-center gap-2 border border-white/10 bg-white/[0.04] px-3 py-1">
            <Compass className="size-3 text-accent" aria-hidden="true" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-white/65">
              Interactive demand preview
            </span>
          </div>

          <h2 className="font-serif text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl lg:text-5xl">
            Inspect the demand signal before you build the first screen
          </h2>

          <p className="mx-auto mt-4 max-w-xl font-mono text-sm leading-[1.7] text-white/65 sm:text-base lg:text-lg">
            Pick a niche, run the preview, open the evidence, and decide whether
            the gated prompt is worth unlocking.
          </p>

          {/* Tool badge — tight, monospace */}
          <div className="mt-5 inline-flex items-center gap-2 border border-white/10 bg-white/[0.04] px-3 py-1.5">
            <Sparkles className="size-3 text-accent" aria-hidden="true" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-white/65">
              No signup for preview
            </span>
            <div className="h-3 w-px bg-white/10" />
            <span className="font-mono text-[11px] text-white/45">
              Open the evidence first, then apply to unlock the prompt.
            </span>
          </div>
        </div>

        {/* Demo runner — clean border, evidence surface */}
        <div className="mx-auto w-full max-w-5xl rounded-xl border border-white/10 bg-white/[0.03] p-1">
          <DemoRunner
            initialPreview={samplePreview}
            persistedPreviewMap={persistedPreviewMap}
            onPreviewVisible={setVisiblePreview}
            onRestrictedJoinWaitlist={handleJoinWaitlist}
          />
        </div>
      </div>

      <WaitlistModal
        analyticsSource="guided_demo"
        analyticsContext={
          visiblePreview
            ? {
                nicheSlug: visiblePreview.nicheSlug,
                resultKind: visiblePreview.kind,
              }
            : undefined
        }
        open={waitlistOpen}
        onOpenChange={setWaitlistOpen}
        hideTrigger
      />
    </section>
  );
}
